import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { ThreeBackground } from '../components/ThreeBackground';
import { TiltCard } from '../components/TiltCard';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      setError('Your session has expired. Please log in again.');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);
    setLoading(false);

    if (result.success) {
      if (result.user.role === 'candidate') {
        navigate('/student/dashboard');
      } else if (result.user.role === 'teacher') {
        navigate('/teacher/dashboard');
      } else if (result.user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (result.user.role === 'staff') {
        navigate('/staff/dashboard');
      } else if (result.user.role === 'recruiting_partner') {
        navigate('/recruiting/dashboard');
      } else if (result.user.role === 'sourcing_partner') {
        navigate('/sourcing/dashboard');
      } else {
        navigate('/');
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-space-blue">
      <ThreeBackground />

      <div className="container mx-auto px-4 relative z-10 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700 p-8 rounded-3xl shadow-2xl">
            <div className="text-center mb-8">
              <Link to="/" className="inline-block mb-6">
                <div className="text-center">
                  <span className="text-2xl font-black text-white tracking-tight block">
                    Poly<span className="text-cyan-500">glots</span>
                  </span>
                  <span className="text-sm font-bold text-slate-400 tracking-[0.2em] uppercase">
                    Talent Portal
                  </span>
                </div>
              </Link>
              <h1 className="text-2xl font-bold text-white mb-2">
                Welcome Back
              </h1>
              <p className="text-slate-400">
                Sign in to access your dashboard
              </p>
            </div>


            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-bold flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                  {error}
                </motion.div>
              )}

              <Input
                label={t('common.email')}
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                icon={<Mail className="w-5 h-5" />}
              />

              <div className="space-y-2">
                <div className="flex flex-wrap justify-between items-end mb-1.5 ml-1 gap-2">
                  <label className="text-sm font-bold text-slate-300 uppercase tracking-widest opacity-70">
                    {t('common.password')}
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold text-cyan-400 hover:text-cyan-300 transition-colors uppercase tracking-widest"
                  >
                    {t('auth.forgotPassword.title')}?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    icon={<Lock className="w-5 h-5" />}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[10px] text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                size="lg"
                rightIcon={!loading && <ArrowRight className="w-5 h-5" />}
              >
                {loading ? (i18n.language === 'de' ? 'Anmeldung...' : 'Logging in...') : t('navigation.login')}
              </Button>


              <p className="text-center text-sm text-slate-400 font-medium pt-2">
                {t('auth.dontHaveAccount')}{' '}
                <Link to="/register" className="text-cyan-400 hover:text-cyan-300 font-bold ml-1 transition-colors">
                  {t('auth.signUp')}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
